import empty from "is-empty";
import { Settings } from "./types";
import { SettingsSelectors } from "./selectors";
import { AuthSelectors } from "src/redux/Auth/selectors";
import { ModalActions } from "src/redux/Modal/actions";
import { MdActions } from "src/redux/MarketData/actions";
import { ToasterActions } from "src/redux/Toaster/actions";
import fetchWrapper from "src/utils/fetchWrapper";
import AutoOfferSettings from "src/components/views/TopBar/AutoOfferSettings";
import AccountSettings from "src/components/views/TopBar/AccountSettings";
import AvailabilitySettings from "src/components/views/TopBar/AvailabilitySettings";
import LenderBlockedSecuritiesSettings from "src/components/views/TopBar/LenderBlockedSecuritiesSettings";
import ListSettings from "src/components/views/TopBar/ListSettings";
import EmailSettings from "src/components/views/TopBar/EmailSettings";
import IdentifierPreferences from "src/components/views/TopBar/IdentifierPreferences";
import FundSettings from "src/components/views/TopBar/FundSettings";
import CurrencyConverter from "src/utils/CurrencyConversionService";
import PersonalSettings from "src/components/views/TopBar/PersonalSettings";
import { SpinnerActions } from "src/redux/Spinner/actions";
import * as Validators from "src/utils/validators";
import { ConfirmActions } from "../Confirm/actions";
import { TopBarSelectors } from "src/redux/TopBar/selectors";
import { ROLE } from "src/redux/TopBar/constants";
import { CounterPartysSelectors } from "src/redux/CounterPartys/selectors";
import PeerCptySettings from "src/components/views/TopBar/PeerCptySettings";
import { CounterPartys } from "src/redux/CounterPartys/types";
import AccountSettingsV2 from "src/components/views/TopBar/AccountSettingsV2";
import isEmpty from "is-empty";
import { getConfig } from "src/utils/config";

// One feature-flags poll loop per tab. Held at module scope so a re-login can
// clear the previous loop before arming a new one - otherwise every re-auth
// stacks another permanent 5s poller and the request rate climbs for as long
// as the tab stays open.
let featureFlagsPollHandle = null;

const mapUiCptyToApiCpty = (uiCpty) => ({
    cptyCode: uiCpty.cptyCode,
    name: uiCpty.isUnregisteredCpty ? uiCpty.cptyName : undefined,
    unregistered: uiCpty.isUnregisteredCpty ?? false,
    defaultObjective: uiCpty.defaultObjective,
    emails: uiCpty.emails,
    secret: uiCpty.secret,
});

const mapUiCptyToDirCpty = (uiCpty) => ({
    id: uiCpty.cptyCode,
    name: uiCpty.cptyName,
    myCpty: false,
    unregistered: uiCpty.isUnregisteredCpty,
    secret: uiCpty.secret,
});

// A user who has never saved Personal Settings has no stored opt-ins, and the
// notification reactors can only email somebody whose saved settings say so. The
// toggles therefore render off, matching what the server holds: showing them on
// would promise mail that is never sent.
const UNSET_EMAIL_OPT_INS = Object.freeze({
    locatesReceived: false,
    offersReceived: false,
    recallsReceived: false,
    newAvailabilityReceived: false,
    eaUpgradeRequestReceived: false,
});

export const SettingsActions = {
    GetLenderSettings: () => {
        return (dispatch, getState) => {
            return dispatch(
                fetchWrapper(
                    AuthSelectors.BuildLenderFetchSettingsUrl(getState())
                )
            ).then((result) => {
                dispatch(SettingsActions.UpdateLenderSettings(result));
                return Promise.resolve(result);
            });
        };
    },

    GetBorrowerSettings: () => {
        return (dispatch, getState) => {
            return dispatch(
                fetchWrapper(
                    AuthSelectors.BuildBorrowerFetchSettingsUrl(getState())
                )
            ).then((result) => {
                dispatch(SettingsActions.UpdateBorrowerSettings(result));
                return Promise.resolve(result);
            });
        };
    },

    UpdateLenderSettings: (settings) => {
        return (dispatch) => {
            return dispatch({
                type: Settings.UPDATE_LENDER_SETTINGS,
                settings,
            });
        };
    },

    UpdateBorrowerSettings: (settings) => {
        return (dispatch) => {
            return dispatch({
                type: Settings.UPDATE_BORROWER_SETTINGS,
                settings,
            });
        };
    },

    UpdatePeerCptySettings: (settings) => (dispatch) =>
        dispatch({
            type: Settings.UPDATE_PEER_CPTY_SETTINGS,
            settings,
        }),

    GetIdentifierPreferences: () => {
        return (dispatch) => {
            dispatch(SpinnerActions.Open());
            return dispatch(SettingsActions.GetUserSettings())
                .then(() => {
                    dispatch(SpinnerActions.Close());
                    dispatch(
                        ModalActions.Open(IdentifierPreferences, {
                            width: 550,
                            height: 460,
                            ignoreClickOutside: true,
                            title: "Identifier Preferences",
                            settings: true,
                        })
                    );
                })
                .catch(() => {
                    dispatch(ModalActions.Close());
                    dispatch(SpinnerActions.Close());
                    dispatch(
                        ToasterActions.NewError(
                            "Error Getting Identifier Preferences"
                        )
                    );
                });
        };
    },

    GetAOSettings: () => {
        return (dispatch) => {
            dispatch(SpinnerActions.Open());

            return dispatch(SettingsActions.GetLenderSettings())
                .then(() => {
                    dispatch(SpinnerActions.Close());
                    dispatch(
                        ModalActions.Open(AutoOfferSettings, {
                            width: 696,
                            height: 595,
                            ignoreClickOutside: true,
                            title: "Auto-Offer Settings",
                            settings: true,
                        })
                    );
                })
                .catch(() => {
                    dispatch(ModalActions.Close());
                    dispatch(SpinnerActions.Close());
                    dispatch(
                        ToasterActions.NewError(
                            "Error Getting Auto-Offer Settings"
                        )
                    );
                });
        };
    },

    GetAccountSettings: () => {
        return (dispatch, getState) => {
            dispatch(SpinnerActions.Open());
            const {
                "enable-multi-account-code-support":
                    enabledMultipleAccountSupport,
            } = SettingsSelectors.FeatureFlags(getState());
            return dispatch(SettingsActions.GetCPTYSettingsForEachRole())
                .then(() => {
                    dispatch(SpinnerActions.Close());
                    dispatch(
                        enabledMultipleAccountSupport
                            ? ModalActions.Open(AccountSettingsV2, {
                                  width: 500,
                                  height: 470,
                                  ignoreClickOutside: true,
                                  title: "Intermediary Accounts",
                                  settings: true,
                              })
                            : ModalActions.Open(AccountSettings, {
                                  width: 435,
                                  height: 400,
                                  ignoreClickOutside: true,
                                  title: "Intermediary Accounts",
                                  settings: true,
                              })
                    );
                })
                .catch(() => {
                    dispatch(ModalActions.Close());
                    dispatch(SpinnerActions.Close());
                    dispatch(ToasterActions.NewError("Error Account Settings"));
                });
        };
    },

    GetAvailabilitySettings: () => {
        return (dispatch, getState) => {
            dispatch(SpinnerActions.Open());
            return dispatch(SettingsActions.GetLenderSettings())
                .then(() => {
                    dispatch(SpinnerActions.Close());
                    dispatch(
                        ModalActions.Open(AvailabilitySettings, {
                            width: 500,
                            height: 535,
                            ignoreClickOutside: true,
                            title: "Outbox Settings",
                            settings: true,
                            customProps: {
                                availabilitySettings:
                                    SettingsSelectors.AvailabilitySettings(
                                        getState()
                                    ),
                                onSave: (settings) =>
                                    dispatch(
                                        SettingsActions.SaveCPTYSettings(
                                            false,
                                            { availabilitySettings: settings }
                                        )
                                    ),
                                isListLevel: false,
                            },
                        })
                    );
                })
                .catch(() => {
                    dispatch(ModalActions.Close());
                    dispatch(SpinnerActions.Close());
                    dispatch(
                        ToasterActions.NewError(
                            "Error Getting Auto-Offer Settings"
                        )
                    );
                });
        };
    },

    GetLenderBlockedSecuritySettings: () => {
        return (dispatch, getState) => {
            dispatch(SpinnerActions.Open());
            return dispatch(SettingsActions.GetLenderSettings())
                .then(async () => {
                    const currentBlockedSecurities =
                        SettingsSelectors.LenderBlockedSecuritiesSettings(
                            getState()
                        );

                    // We only have IDs, update times, and usernames from the UI cpty endpoint,
                    // fill in the missing stock data if any are set.
                    if (!isEmpty(currentBlockedSecurities)) {
                        const securityIDs = currentBlockedSecurities.map(
                            (e) => e.securityId
                        );

                        const mdata = await dispatch(
                            MdActions.GetMarketDataBatch(securityIDs)
                        );
                        const stocks = Object.values(mdata).map(
                            (item) => item.data
                        );
                        const merged = currentBlockedSecurities.map(
                            (blocked) => {
                                const stock = stocks.find(
                                    (s) => s.id === blocked.securityId
                                );
                                return {
                                    ...blocked,
                                    ...(stock || {}),
                                };
                            }
                        );

                        await dispatch(
                            SettingsActions.SetPendingBlockedSecurity(merged)
                        );
                    } else {
                        // empty out any existing list
                        await dispatch(
                            SettingsActions.SetPendingBlockedSecurity([])
                        );
                    }

                    await dispatch(SpinnerActions.Close());
                    await dispatch(
                        ModalActions.Open(LenderBlockedSecuritiesSettings, {
                            width: 850,
                            height: 600,
                            ignoreClickOutside: true,
                            title: "Restricted Securities Settings",
                            settings: true,
                        })
                    );
                })
                .catch(async () => {
                    await dispatch(ModalActions.Close());
                    await dispatch(SpinnerActions.Close());
                    await dispatch(
                        ToasterActions.NewError(
                            "Error Getting Lender Restricted Securities Settings"
                        )
                    );
                });
        };
    },

    GetListSettings: () => {
        return (dispatch, getState) => {
            dispatch(SpinnerActions.Open());
            const {
                "enable-multi-account-code-support": enabledMultipleAccounts,
                "enable-list-level-availability": isListLevelAvailability,
            } = SettingsSelectors.FeatureFlags(getState());
            return dispatch(SettingsActions.GetLenderSettings())
                .then(() => {
                    dispatch(SpinnerActions.Close());
                    dispatch(
                        ModalActions.Open(ListSettings, {
                            width: enabledMultipleAccounts
                                ? isListLevelAvailability
                                    ? 1480
                                    : 1415
                                : 1115,
                            height: 764,
                            ignoreClickOutside: true,
                            title: "List Settings",
                            settings: true,
                        })
                    );
                })
                .catch(() => {
                    dispatch(ModalActions.Close());
                    dispatch(SpinnerActions.Close());
                    dispatch(
                        ToasterActions.NewError(
                            "Error Getting Auto-Offer Settings"
                        )
                    );
                });
        };
    },

    GetPeerCptySettings: () => (dispatch, getState) => {
        const url = AuthSelectors.BuildPeerCptySettingsUrl(getState());

        return dispatch(fetchWrapper(url)).then((result) => {
            dispatch(SettingsActions.UpdatePeerCptySettings(result));
            return Promise.resolve(result);
        });
    },

    GetPeerCptySettingsUi: () => (dispatch) => {
        dispatch(SpinnerActions.Open());
        dispatch(SettingsActions.GetPeerCptySettings())
            .then(() => {
                dispatch(SpinnerActions.Close());

                dispatch(
                    ModalActions.Open(PeerCptySettings, {
                        width: 1000,
                        height: 620,
                        ignoreClickOutside: true,
                        title: "Counterparty Settings",
                        settings: true,
                        onClose: () =>
                            dispatch(SettingsActions.SetNewPeerCptySetting([])),
                    })
                );
            })
            .catch(() => {
                dispatch(SpinnerActions.Close());
                dispatch(
                    ToasterActions.NewError(
                        "Error getting counterparty settings"
                    )
                );
            });
    },

    GetCPTYSettingsForEachRole: () => {
        return (dispatch, getState) => {
            const state = getState();
            return Promise.all([
                dispatch(
                    fetchWrapper(
                        AuthSelectors.BuildLenderFetchSettingsUrl(state)
                    )
                ),
                dispatch(
                    fetchWrapper(
                        AuthSelectors.BuildBorrowerFetchSettingsUrl(state)
                    )
                ),
            ]).then((results) => {
                dispatch(SettingsActions.UpdateLenderSettings(results[0]));
                dispatch(SettingsActions.UpdateBorrowerSettings(results[1]));
            });
        };
    },

    GetEmailSettings: () => {
        return (dispatch, getState) => {
            dispatch(SpinnerActions.Open());
            const {
                "enable-custom-confirmation-attachment":
                    enabledCustomConfirmationAttachment,
            } = SettingsSelectors.FeatureFlags(getState());
            return dispatch(SettingsActions.GetCPTYSettingsForEachRole())
                .then(() => {
                    dispatch(SpinnerActions.Close());
                    dispatch(
                        ModalActions.Open(EmailSettings, {
                            width: 684,
                            height: enabledCustomConfirmationAttachment
                                ? 710
                                : 500,
                            ignoreClickOutside: true,
                            title: "Records Settings",
                            settings: true,
                        })
                    );
                })
                .catch(() => {
                    dispatch(ModalActions.Close());
                    dispatch(SpinnerActions.Close());
                    dispatch(ToasterActions.NewError("Error Getting Emails"));
                });
        };
    },

    OpenUserSettings: () => {
        return (dispatch) => {
            dispatch(SpinnerActions.Open());
            return dispatch(SettingsActions.GetUserSettings())
                .then(() => {
                    dispatch(SpinnerActions.Close());
                    dispatch(
                        ModalActions.Open(PersonalSettings, {
                            width: 550,
                            height: 520,
                            ignoreClickOutside: true,
                            title: "Personal Settings",
                            settings: true,
                        })
                    );
                })
                .catch(() => {
                    dispatch(ModalActions.Close());
                    dispatch(SpinnerActions.Close());
                    dispatch(
                        ToasterActions.NewError("Error Getting User Settings")
                    );
                });
        };
    },

    GetFundSettings: () => {
        return (dispatch, getState) => {
            dispatch(SpinnerActions.Open());
            const {
                "enable-multi-account-code-support": enabledMultipleAccounts,
            } = SettingsSelectors.FeatureFlags(getState());
            return dispatch(SettingsActions.GetBorrowerSettings())
                .then(() => {
                    dispatch(SpinnerActions.Close());
                    dispatch(
                        ModalActions.Open(FundSettings, {
                            width: enabledMultipleAccounts ? 1151 : 851,
                            height: 784,
                            ignoreClickOutside: true,
                            title: "Fund Settings",
                            settings: true,
                        })
                    );
                })
                .catch(() => {
                    dispatch(ModalActions.Close());
                    dispatch(SpinnerActions.Close());
                    dispatch(
                        ToasterActions.NewError("Error Getting Fund Settings")
                    );
                });
        };
    },

    UpdateAOSettings: (settings) => {
        return (dispatch) => {
            dispatch({
                type: Settings.UPDATE_AO_SETTINGS,
                settings,
            });
        };
    },

    UpdateAccountSettings: (accountType, settings) => {
        return (dispatch) => {
            dispatch({
                type: Settings.UPDATE_ACCOUNT_SETTINGS,
                accountType,
                settings,
            });
        };
    },

    SetGeneralAccountSettings: (accounts) => (dispatch) => {
        dispatch({
            type: Settings.SET_GENERAL_ACCOUNT_SETTING,
            accounts,
        });
    },

    UpdateEmailSettings: (settings) => {
        return (dispatch) => {
            dispatch({
                type: Settings.UPDATE_EMAIL_SETTINGS,
                settings,
            });
        };
    },

    UpdateConfirmationFileFields: (fields) => (dispatch) =>
        dispatch({
            type: Settings.UPDATE_CONFIRMATION_FILE_FIELDS,
            fields,
        }),

    UpdateDeletedListSettings: (list) => {
        return (dispatch) => {
            dispatch({
                type: Settings.SET_DELETED_LIST,
                list,
            });
        };
    },

    AddPendingBlockedSecurity: (stock) => {
        return (dispatch) => {
            dispatch({
                type: Settings.ADD_PENDING_BLOCKED_SECURITY,
                stock,
            });
            return Promise.resolve();
        };
    },

    RemovePendingBlockedSecurity: (securityId) => {
        return (dispatch) => {
            dispatch({
                type: Settings.REMOVE_PENDING_BLOCKED_SECURITY,
                securityId,
            });
            return Promise.resolve();
        };
    },

    SetPendingBlockedSecurity: (stocksList) => {
        return (dispatch) => {
            dispatch({
                type: Settings.SET_PENDING_BLOCKED_SECURITY,
                stocksList,
            });
            return Promise.resolve();
        };
    },

    AddListSettings: (newSetting) => {
        return (dispatch, getState) => {
            const currentSettings = SettingsSelectors.ListSettings(getState());
            const dynamicAndClientPoolList = SettingsSelectors.DynamicAndClientPoolList(getState());
            const { "enable-multi-account-code-support": isAccountSelectable } =
                SettingsSelectors.FeatureFlags(getState());
            //TODO: error message should be changed
            const errors = Validators.getListValidationErrors(
                newSetting,
                currentSettings,
                dynamicAndClientPoolList,
                isAccountSelectable
            );
            if (!empty(errors)) {
                dispatch(SettingsActions.SetDisplayNewListErrors(true));
                return Promise.reject(errors);
            }

            if (empty(newSetting.callable)) newSetting.callable = false;
            if (empty(newSetting.dividend)) newSetting.dividend = 100;
            if (empty(newSetting.collType)) newSetting.collType = "NC";
            newSetting.advertiseAvailabilitySettings = {
                enabled: false,
                advertiseRate: false,
                availabilityCptyIds: [],
            };
            newSetting.isNew = true;

            // A draft, not a list. It renders in this dialog's table and is
            // wiped when the dialog closes; the confirmed list arrives from the
            // backend through the settings load or the CPTY_SETTINGS_UPDATED
            // push. See docs/plans/list-settings-pending-drafts.md.
            dispatch({
                type: Settings.ADD_PENDING_LIST_SETTINGS,
                newSetting,
            });
            dispatch(SettingsActions.SetDisplayNewListErrors(false));
            return Promise.resolve();
        };
    },

    EditAvailability: (listName, advertiseAvailabilitySettings) => {
        return (dispatch) =>
            dispatch({
                type: Settings.UPDATE_LIST_SETTINGS,
                listName,
                updates: { advertiseAvailabilitySettings, hasUpdates: true },
            });
    },

    /** Drop every unconfirmed draft. Called when the list-settings dialog
     *  closes, so no draft survives to the next open. */
    ClearPendingListSettings: () => ({
        type: Settings.CLEAR_PENDING_LIST_SETTINGS,
    }),

    RemoveListSettings: (params) => {
        return (dispatch) => {
            if (params.isNew) {
                return dispatch({
                    type: Settings.REMOVE_LIST_SETTINGS,
                    name: params.name,
                });
            }
            const message = `Deleting a list will remove all inventory associated with that list. Are you sure you wish to continue?`;
            dispatch(
                ConfirmActions.Open({
                    message,
                    title: "Delete",
                    cancelTitle: "Cancel",
                    okTitle: "Ok",
                    testId: "list-setting-remove",
                    backdrop: true,
                    onOk: () =>
                        dispatch({
                            type: Settings.REMOVE_LIST_SETTINGS,
                            name: params.name,
                        }),
                })
            );
        };
    },

    SetListSettings: (params) => {
        return (dispatch) => {
            dispatch({
                type: Settings.SET_LIST_SETTINGS,
                listSettings: params,
            });
        };
    },

    SetFundSettings: (params) => {
        return (dispatch) => {
            dispatch({
                type: Settings.SET_FUND_SETTINGS,
                fundSettings: params,
            });
        };
    },

    UpdateDeletedFundSettings: (list) => {
        return (dispatch) => {
            dispatch({
                type: Settings.SET_DELETED_FUND,
                list,
            });
        };
    },
    AddFundSettings: (newSetting) => {
        return (dispatch, getState) => {
            console.log("[AddFundSettings] Input:", JSON.stringify(newSetting));
            const currentSettings = SettingsSelectors.FundSettings(getState());
            const { "enable-multi-account-code-support": isAccountSelectable } =
                SettingsSelectors.FeatureFlags(getState());
            const errors = Validators.getFundValidationErrors(
                newSetting,
                currentSettings,
                isAccountSelectable
            );

            if (!empty(errors)) {
                console.log("[AddFundSettings] Validation errors:", errors);
                dispatch(SettingsActions.SetDisplayNewFundErrors(true));
                return Promise.reject(errors);
            }

            newSetting.isNew = true;
            newSetting.fundName = newSetting.fundName.trim();
            newSetting.internalFundCode = newSetting.internalFundCode?.trim();
            newSetting.ksdFundCode = newSetting.ksdFundCode?.trim();
            newSetting.ksfcFundCode = newSetting.ksfcFundCode?.trim();

            console.log("[AddFundSettings] Adding to state:", JSON.stringify(newSetting));
            dispatch({
                type: Settings.ADD_FUND_SETTINGS,
                newSetting,
            });
            dispatch(SettingsActions.SetDisplayNewFundErrors(false));
            return Promise.resolve();
        };
    },

    RemoveFund: (params) => {
        return (dispatch) => {
            return dispatch({
                type: Settings.REMOVE_FUND_SETTINGS,
                fundName: params.fundName,
            });
        };
    },
    SaveCPTYSettingsForEveryRole: () => {
        return (dispatch, getState) => {
            dispatch(SpinnerActions.Open());

            const state = getState();
            const url = AuthSelectors.BuildPutSettingsUrl(state);
            const method = "PUT";
            const headers = {
                Accept: "application/json",
                "Content-Type": "application/json",
            };

            // Update both (lender, borrower)
            const promises = [
                dispatch(
                    fetchWrapper(url, {
                        method,
                        headers,
                        body: JSON.stringify(
                            SettingsSelectors.Settings(state, ROLE.BORROWER)
                        ),
                    })
                ),
                dispatch(
                    fetchWrapper(url, {
                        method,
                        headers,
                        body: JSON.stringify(
                            SettingsSelectors.Settings(state, ROLE.LENDER)
                        ),
                    })
                ),
            ];

            return Promise.all(promises)
                .then(() => {
                    dispatch(ModalActions.Close());
                    dispatch(SpinnerActions.Close());
                })
                .catch(() => {
                    dispatch(SpinnerActions.Close());
                    dispatch(
                        ToasterActions.NewError(
                            "Error Updating Lender/Borrower CPTY Settings"
                        )
                    );
                });
        };
    },
    SaveCPTYSettings: (updateListOrder, updates = {}) => {
        return async (dispatch, getState) => {
            dispatch(SpinnerActions.Open());
            const state = getState();
            const url = AuthSelectors.BuildPutSettingsUrl(state);
            const settings = SettingsSelectors.Settings(
                state,
                TopBarSelectors.Role(state)
            );
            try {
                await dispatch(
                    fetchWrapper(url, {
                        method: "PUT",
                        body: JSON.stringify({ ...settings, ...updates }),
                        headers: {
                            Accept: "application/json",
                            "Content-Type": "application/json",
                        },
                    })
                );

                if (updateListOrder) {
                    // Workaround: Sending two requests in quick succession can cause a
                    // race condition in the backend where the previous request gets discarded.
                    await sleep(1000);

                    const url = AuthSelectors.BuildPutLenderListOrderUrl(state);
                    await dispatch(
                        fetchWrapper(url, {
                            method: "PUT",
                            body: JSON.stringify({
                                order: SettingsSelectors.ListSettingsWithOrder(
                                    state
                                ),
                            }),
                            headers: {
                                Accept: "application/json",
                                "Content-Type": "application/json",
                            },
                        })
                    );
                }

                await dispatch(ModalActions.Close());
                await dispatch(SpinnerActions.Close());
            } catch (e) {
                await dispatch(SpinnerActions.Close());
                await dispatch(
                    ToasterActions.NewError(
                        "Error Updating Auto-Offer Settings"
                    )
                );
            }
        };
    },
    SaveLenderBlockedSecurities: (payload) => {
        return async (dispatch, getState) => {
            dispatch(SpinnerActions.Open());
            const state = getState();
            const url = AuthSelectors.BuildPutLenderBlockedSecuritiesUrl(state);

            try {
                await dispatch(
                    fetchWrapper(url, {
                        method: "PUT",
                        body: JSON.stringify(payload),
                        headers: {
                            Accept: "application/json",
                            "Content-Type": "application/json",
                        },
                    })
                );

                await dispatch(ModalActions.Close());
                await dispatch(SpinnerActions.Close());
            } catch (e) {
                await dispatch(SpinnerActions.Close());
                await dispatch(
                    ToasterActions.NewError(
                        "Error Updating lender restricted securities list"
                    )
                );
            }
        };
    },

    updateFundSettings: () => {
        return (dispatch, getState) => {
            dispatch(SpinnerActions.Open());
            const state = getState();
            const url = AuthSelectors.BuildPutBorrowerFundSettingsUrl(state);
            const payload = SettingsSelectors.BorrowerFundSettings(state);
            console.log("[updateFundSettings] URL:", url);
            console.log("[updateFundSettings] All fund settings in state:", JSON.stringify(state.Settings.fundSettings));
            console.log("[updateFundSettings] Payload (upsert/delete):", JSON.stringify(payload));
            return dispatch(
                fetchWrapper(url, {
                    method: "PUT",
                    body: JSON.stringify(payload),
                    headers: {
                        Accept: "application/json",
                        "Content-Type": "application/json",
                    },
                })
            )
                .then(() => {
                    console.log("[updateFundSettings] Save succeeded");
                    dispatch(SettingsActions.UpdateDeletedFundSettings([]));
                    dispatch(ModalActions.Close());
                    dispatch(SpinnerActions.Close());
                })
                .catch((err) => {
                    console.error("[updateFundSettings] Save failed:", err);
                    dispatch(SpinnerActions.Close());
                    dispatch(
                        ToasterActions.NewError(
                            "Error Updating Auto-Offer Settings"
                        )
                    );
                });
        };
    },

    updateLenderListSettings: () => {
        return (dispatch, getState) => {
            dispatch(SpinnerActions.Open());
            const state = getState();
            const url = AuthSelectors.BuildPutLenderListSettingsUrl(state);
            return dispatch(
                fetchWrapper(url, {
                    method: "PUT",
                    body: JSON.stringify(
                        SettingsSelectors.LenderListSettings(state)
                    ),
                    headers: {
                        Accept: "application/json",
                        "Content-Type": "application/json",
                    },
                })
            )
                .then(() => {
                    dispatch(SettingsActions.UpdateDeletedListSettings([]));
                    dispatch(ModalActions.Close());
                    dispatch(SpinnerActions.Close());
                })
                .catch(() => {
                    dispatch(SpinnerActions.Close());
                    dispatch(
                        ToasterActions.NewError("Error Updating List Settings")
                    );
                });
        };
    },

    SavePeerCptySettings:
        (changeSet, newSet, deleteSet) => (dispatch, getState) => {
            if (empty(changeSet) && empty(newSet) && empty(deleteSet)) {
                dispatch(ModalActions.Close());
                return;
            }

            const newPeerCptySettings = {
                updateCpties: Object.values(changeSet)
                    .concat(newSet)
                    .map(mapUiCptyToApiCpty),
                removeCpties: deleteSet,
            };

            if (
                empty(newPeerCptySettings.updateCpties) &&
                empty(newPeerCptySettings.removeCpties)
            ) {
                dispatch(ModalActions.Close());
                dispatch(SpinnerActions.Close());
                return;
            }

            const state = getState();
            const url = AuthSelectors.BuildPeerCptySettingsUrl(state);
            const currPeerCptySettings =
                SettingsSelectors.PeerCptySettings(state);

            // calculate cpty directory entries derived from new peer cpty settings
            let allCptys = CounterPartysSelectors.GetAllCptysById(state);
            Object.keys(allCptys).forEach((c) => {
                if (deleteSet.includes(c)) {
                    delete allCptys[c];
                }
            });
            const newCptys = Object.values(allCptys).concat(
                newSet.map(mapUiCptyToDirCpty)
            );

            dispatch(
                fetchWrapper(url, {
                    method: "PUT",
                    body: JSON.stringify(newPeerCptySettings),
                    headers: {
                        Accept: "application/json",
                        "Content-Type": "application/json",
                    },
                })
            )
                .then(() => {
                    dispatch(SettingsActions.SetNewPeerCptySetting([]));
                    dispatch(ModalActions.Close());
                    dispatch(SpinnerActions.Close());
                    // persist successful update
                    dispatch({
                        type: Settings.UPDATE_PEER_CPTY_SETTINGS,
                        settings: currPeerCptySettings
                            .filter((s) => !deleteSet.includes(s.cptyCode))
                            .map((s) => changeSet[s.cptyCode] ?? s)
                            .concat(newSet),
                    });
                    // update counterparties too
                    dispatch({
                        type: CounterPartys.SET,
                        result: { cptys: newCptys },
                    });
                })
                .catch(() => {
                    dispatch(SpinnerActions.Close());
                    dispatch(
                        ToasterActions.NewError(
                            "Error saving lender's peer cpty settings"
                        )
                    );
                });
        },

    SetUserSettings: (userSettings) => {
        return (dispatch) => {
            CurrencyConverter.setTargetCurrency(
                userSettings.preferredCcy || ""
            );
            return dispatch({
                type: Settings.UPDATE_USER_SETTINGS,
                userSettings,
            });
        };
    },

    GetUserSettings: () => {
        return (dispatch, getState) => {
            const state = getState();
            const url = AuthSelectors.BuildUserSettingsUrl(state);
            return dispatch(fetchWrapper(url))
                .then((result) => {
                    let tempResult = result;
                    if (!Object.keys(result).includes("emailOptIns")) {
                        tempResult.emailOptIns = UNSET_EMAIL_OPT_INS;
                    }
                    return dispatch(
                        SettingsActions.SetUserSettings(tempResult)
                    );
                })
                .catch((error) => {
                    // NOTE: 404 when there's no user settings
                    return dispatch(
                        SettingsActions.SetUserSettings({
                            emailOptIns: UNSET_EMAIL_OPT_INS,
                        })
                    );
                });
        };
    },

    UpdateAndCloseModal: (updateAsycFunction) => {
        return (dispatch, getState) => {
            dispatch(SpinnerActions.Open());
            dispatch(updateAsycFunction).finally(() => {
                dispatch(ModalActions.Close());
                dispatch(SpinnerActions.Close());
            });
        };
    },

    UpdateUserSettings: (userSettings) => {
        return (dispatch, getState) => {
            const url = AuthSelectors.BuildUserSettingsUrl(getState());
            const currUserSettings =
                userSettings ?? SettingsSelectors.UserSettings(getState());
            return dispatch(
                fetchWrapper(url, {
                    method: "PUT",
                    body: JSON.stringify(currUserSettings),
                    headers: {
                        Accept: "application/json",
                        "Content-Type": "application/json",
                    },
                })
            )
                .then(() => {
                    dispatch(SettingsActions.SetUserSettings(currUserSettings));
                })
                .catch((error) => {
                    dispatch(
                        ToasterActions.NewError(
                            "Error at Updating User Settings"
                        )
                    );
                });
        };
    },

    SetDisplayNewListErrors: (value) => {
        return (dispatch) => {
            return dispatch({
                type: Settings.DISPLAY_NEW_LIST_ERRORS,
                value,
            });
        };
    },

    SetDisplayNewFundErrors: (value) => {
        return (dispatch) => {
            return dispatch({
                type: Settings.DISPLAY_NEW_FUND_ERRORS,
                value,
            });
        };
    },

    // Fetch feature flags from the server and start polling for changes.
    // Flags are evaluated server-side per cpty; the SPA reads the result.
    InitFeatureFlags: () => async (dispatch, getState) => {
        const flagsUrl = getState().Auth.settingsUrl + "/feature-flags";
        const flags = await dispatch(fetchWrapper(flagsUrl));
        dispatch({
            type: Settings.SET_LD_FEATURE_FLAGS,
            flags,
        });

        // Poll for flag changes, dispatching UPDATE_LD_FEATURE_FLAGS only for
        // flags whose value actually changed. Cadence is per-environment
        // (config.json featureFlagsPollMs): fast in dev/test, slow in demo/prod
        // since flags rarely change at runtime. Clear any prior loop first so a
        // re-login replaces its poller instead of stacking one.
        const pollMs = getConfig().featureFlagsPollMs ?? 60000;
        if (featureFlagsPollHandle) {
            clearInterval(featureFlagsPollHandle);
        }
        featureFlagsPollHandle = setInterval(async () => {
            try {
                const updated = await dispatch(fetchWrapper(flagsUrl));
                const current = SettingsSelectors.FeatureFlags(getState());
                const changes = {};
                let hasChanges = false;
                for (const [key, value] of Object.entries(updated)) {
                    if (current[key] !== value) {
                        changes[key] = value;
                        hasChanges = true;
                    }
                }
                if (hasChanges) {
                    dispatch({
                        type: Settings.UPDATE_LD_FEATURE_FLAGS,
                        flagChanges: changes,
                    });
                }
            } catch (e) {
                console.warn("Feature flag poll failed:", e);
            }
        }, pollMs);
    },

    // Stop the feature-flags poll loop (called on logout so a tab left on the
    // login screen does not keep polling).
    StopFeatureFlagsPolling: () => () => {
        if (featureFlagsPollHandle) {
            clearInterval(featureFlagsPollHandle);
            featureFlagsPollHandle = null;
        }
    },
    SetSelectedPeerCptySetting: (data) => {
        return (dispatch) => {
            dispatch({
                type: Settings.SET_SELECTED_PEER_CPTY_SETTING,
                payload: data,
            });
        };
    },

    SetNewPeerCptySetting: (data) => {
        return (dispatch) => {
            dispatch({
                type: Settings.SET_NEW_PEER_CPTY_SETTING,
                payload: data,
            });
        };
    },

    UpdateCptySecretKey: (data) => {
        return (dispatch) => {
            dispatch({
                type: Settings.UPDATE_CPTY_SECRET_KEY,
                payload: data,
            });
        };
    },

    ResetSecretKey: (data) => (dispatch, getState) => {
        if (empty(data)) {
            dispatch(ModalActions.Close());
            return;
        }

        const cptySettings = {
            updateCpties: [data],
            removeCpties: [],
        };

        const state = getState();
        const url = AuthSelectors.BuildPeerCptySettingsUrl(state);
        dispatch(SpinnerActions.Open());
        dispatch(
            fetchWrapper(url, {
                method: "PUT",
                body: JSON.stringify(cptySettings),
                headers: {
                    Accept: "application/json",
                    "Content-Type": "application/json",
                },
            })
        )
            .then((res) => {
                dispatch(SettingsActions.SetNewPeerCptySetting([]));
                dispatch(ModalActions.Close());
                dispatch(SpinnerActions.Close());
                if ("correlationId" in res) {
                    dispatch(SettingsActions.UpdateCptySecretKey(data));
                    dispatch(SettingsActions.SetSelectedPeerCptySetting(data));
                    dispatch(
                        ToasterActions.NewOK("Secret key reset successfully.")
                    );
                } else if ("message" in res) {
                    dispatch(ToasterActions.NewError(res?.message));
                } else {
                    dispatch(
                        ToasterActions.NewError("Error reseting secret key.")
                    );
                }
            })
            .catch(() => {
                dispatch(SpinnerActions.Close());
                dispatch(ToasterActions.NewError("Error reseting secret key."));
            })
            .finally(() => {
                dispatch(SpinnerActions.Close());
            });
    },
};

function sleep(ms) {
    return new Promise((resolve) => setTimeout(resolve, ms));
}
